import { apiClient } from "@/lib/api/client";
import type {
  DashboardDto,
  DashboardWidgetCreateDto,
  DashboardWidgetDto,
  DashboardWidgetUpdateDto,
  DeviceDto,
} from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export type DashboardCreateBody = {
  tenant_id: string;
  name: string;
  description?: string | null;
  is_global?: boolean;
  assigned_user_ids?: string[];
  layout?: JsonObject | null;
};

export type DashboardUpdateBody = {
  name?: string;
  description?: string | null;
  is_global?: boolean;
  assigned_user_ids?: string[];
  layout?: JsonObject | null;
};

export type DashboardListScope = "accessible" | "manage";
export type HmiCanvasValidateResponse = {
  layout: JsonObject;
  migrated: boolean;
  version: number;
};

export type HmiImageAssetUploadDto = {
  path: string;
};

export async function listDashboards(tenantId: string, scope: DashboardListScope = "accessible"): Promise<DashboardDto[]> {
  const { data } = await apiClient.get<DashboardDto[]>("/dashboards", {
    params: { tenant_id: tenantId, scope },
  });
  return data;
}

export async function getDashboard(dashboardId: string): Promise<DashboardDto> {
  const { data } = await apiClient.get<DashboardDto>(`/dashboards/${dashboardId}`);
  return data;
}

export async function listDashboardWidgets(dashboardId: string): Promise<DashboardWidgetDto[]> {
  const { data } = await apiClient.get<DashboardWidgetDto[]>(`/dashboards/${dashboardId}/widgets`);
  return data;
}

export async function listHmiDashboardDevices(dashboardId: string): Promise<DeviceDto[]> {
  const { data } = await apiClient.get<DeviceDto[]>(`/dashboards/${dashboardId}/hmi/devices`);
  return data;
}

export async function createDashboard(body: DashboardCreateBody): Promise<DashboardDto> {
  const { data } = await apiClient.post<DashboardDto>("/dashboards", body);
  return data;
}

export async function updateDashboard(dashboardId: string, body: DashboardUpdateBody): Promise<DashboardDto> {
  const { data } = await apiClient.put<DashboardDto>(`/dashboards/${dashboardId}`, body);
  return data;
}

export async function validateHmiCanvas(layout: JsonObject): Promise<HmiCanvasValidateResponse> {
  const { data } = await apiClient.post<HmiCanvasValidateResponse>("/dashboards/hmi/validate", { layout });
  return data;
}

export async function uploadHmiImageAsset(file: File): Promise<HmiImageAssetUploadDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<HmiImageAssetUploadDto>("/dashboards/hmi/assets/image-upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
}

export async function getHmiImageAssetBlob(path: string, dashboardId?: string | null): Promise<Blob> {
  const { data } = await apiClient.get<Blob>("/dashboards/hmi/assets/image", {
    params: { path, ...(dashboardId ? { dashboard_id: dashboardId } : {}) },
    responseType: "blob",
  });
  return data;
}

export async function deleteHmiImageAsset(path: string): Promise<void> {
  await apiClient.delete("/dashboards/hmi/assets/image", {
    params: { path },
  });
}

export async function deleteDashboard(dashboardId: string): Promise<void> {
  await apiClient.delete(`/dashboards/${dashboardId}`);
}

export async function createDashboardWidget(body: DashboardWidgetCreateDto): Promise<DashboardWidgetDto> {
  const { data } = await apiClient.post<DashboardWidgetDto>("/widgets", body);
  return data;
}

export async function updateDashboardWidget(
  widgetId: string,
  body: DashboardWidgetUpdateDto,
): Promise<DashboardWidgetDto> {
  const { data } = await apiClient.put<DashboardWidgetDto>(`/widgets/${widgetId}`, body);
  return data;
}

export async function deleteDashboardWidget(widgetId: string): Promise<void> {
  await apiClient.delete(`/widgets/${widgetId}`);
}
